/** The ability system of what is added here: entities, attributes, tags, effects and abilities. */

import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import type {
  AbilityDecl,
  ActivityManifest,
  EffectCondition,
  EffectDecl,
  EntityTypeDecl,
} from "@nebulize/sdk";

import type { StateEntity } from "@/lib/runtime";
import { Button, EmptyState, Input, Select, Stack, StatusBadge } from "@/components/ui";

import { useCreateDraft } from "@/features/studio/lib/draft";
import { nextPatch } from "@/features/studio/lib/version";

import { Chip } from "../../components/Chips";
import { Mono, Muted } from "../../components/Text";
import type { SpaceActivities } from "../../spaces/lib/queries";
import type { AddedActivityRow } from "../../types";

import { activationVerdict, cooldownEffect } from "../lib/abilityVerdict";
import { useNow } from "../lib/useNow";

import { LiveMounts, type MountReading } from "./LiveMounts";
import { Section } from "./Section";

/** One added activity and the manifest it pinned, which is where every declaration is read. */
interface Declared {
  row: AddedActivityRow;
  manifest: ActivityManifest;
}

function declaredIn(activities: SpaceActivities, only: string | null): Declared[] {
  return activities.rows
    .filter((row) => only === null || row.added_activity_id === only)
    .map((row) => ({ row, manifest: activities.entryFor(row)?.manifest }))
    .filter((one): one is Declared => one.manifest !== undefined);
}

/** Whether this manifest declares any of the three, which is what decides it is worth a card. */
function holdsAnything(manifest: ActivityManifest): boolean {
  return (
    Object.keys(manifest.entities ?? {}).length > 0 ||
    Object.keys(manifest.effects ?? {}).length > 0 ||
    Object.keys(manifest.abilities ?? {}).length > 0
  );
}

function bound(min: number | null | undefined, max: number | null | undefined): string {
  if (min == null && max == null) return "unbounded";
  return `${min == null ? "" : String(min)}..${max == null ? "" : String(max)}`;
}

function Entity({ name, decl }: { readonly name: string; readonly decl: EntityTypeDecl }) {
  const attributes = Object.entries(decl.attributes ?? {});
  return (
    <div className="cx-row">
      <div className="cx-row-id">
        <div>
          <div className="cx-node-name">
            <Mono>{name}</Mono>
          </div>
          <div>
            <Muted small>
              {`${decl.scope ?? "activity"} scope${decl.facet == null ? "" : `, ${decl.facet} facet`}${
                decl.keyed_by == null ? "" : `, keyed by ${decl.keyed_by}`
              }`}
            </Muted>
          </div>
          {attributes.map(([attribute, held]) => (
            <div key={attribute}>
              <Muted small>
                <Mono>{`${attribute} = ${String(held.default ?? 0)} [${bound(held.min, held.max)}]`}</Mono>
                {held.max_from == null ? "" : ` ceiling from ${held.max_from}`}
              </Muted>
            </div>
          ))}
          {(decl.tags ?? []).length === 0 ? null : (
            <div className="cx-chips">
              {(decl.tags ?? []).map((tag) => (
                <Chip key={tag}>{tag}</Chip>
              ))}
            </div>
          )}
        </div>
      </div>
      <StatusBadge tone="neutral">{`${String(attributes.length)} attr`}</StatusBadge>
    </div>
  );
}

/** A predicate as the one sentence it is. Flat and ANDed, so there is nothing to nest. */
function condition(one: EffectCondition): string {
  const side = one.on ?? "target";
  const parts: string[] = [];
  if (one.attribute != null) {
    if (one.below != null) parts.push(`${one.attribute} below ${String(one.below)}`);
    if (one.at_most != null) parts.push(`${one.attribute} at most ${String(one.at_most)}`);
    if (one.above != null) parts.push(`${one.attribute} above ${String(one.above)}`);
    if (one.at_least != null) parts.push(`${one.attribute} at least ${String(one.at_least)}`);
    if (one.equals != null) parts.push(`${one.attribute} equals ${String(one.equals)}`);
  }
  for (const tag of one.has ?? []) parts.push(`has ${tag}`);
  for (const tag of one.has_not ?? []) parts.push(`has not ${tag}`);
  return `${side}: ${parts.join(", ") || "nothing"}`;
}

function Effect({ name, decl }: { readonly name: string; readonly decl: EffectDecl }) {
  const modifiers = decl.modifiers ?? [];
  const lasts =
    decl.duration === 0
      ? "instant"
      : decl.duration == null
        ? "until removed"
        : `${String(decl.duration)}s`;

  return (
    <div className="cx-row">
      <div className="cx-row-id">
        <div>
          <div className="cx-node-name">
            <Mono>{name}</Mono>
          </div>
          <div>
            <Muted small>
              {`${lasts}${decl.period == null ? "" : `, every ${String(decl.period)}s`}, stacks ${
                decl.stack ?? "refresh"
              }${(decl.max_stacks ?? 1) > 1 ? ` to ${String(decl.max_stacks)}` : ""}`}
            </Muted>
          </div>
          {modifiers.map((one, at) => (
            <div key={`${one.attribute}-${String(at)}`}>
              <Muted small>
                <Mono>{`${one.op} ${one.attribute} by ${String(one.magnitude)}`}</Mono>
                {` ${one.apply ?? "continuous"}${one.per_stack === true ? ", per stack" : ""}`}
              </Muted>
            </div>
          ))}
          {(decl.when ?? []).map((one, at) => (
            <div key={`when-${String(at)}`}>
              <Muted small>{`when ${condition(one)}`}</Muted>
            </div>
          ))}
          {(decl.while ?? []).map((one, at) => (
            <div key={`while-${String(at)}`}>
              <Muted small>{`while ${condition(one)}`}</Muted>
            </div>
          ))}
          {decl.cue_applied == null && decl.cue_expired == null ? null : (
            <div>
              <Muted small>
                {`cues ${decl.cue_applied ?? "none"} on apply, ${decl.cue_expired ?? "none"} on expiry`}
              </Muted>
            </div>
          )}
          {(decl.tags ?? []).length === 0 ? null : (
            <div className="cx-chips">
              {(decl.tags ?? []).map((tag) => (
                <Chip key={tag}>{tag}</Chip>
              ))}
            </div>
          )}
        </div>
      </div>
      {modifiers.some((one) => (one.apply ?? "continuous") === "periodic") ? (
        <StatusBadge tone="warning">writes per period</StatusBadge>
      ) : (
        <StatusBadge tone="success">free to hold</StatusBadge>
      )}
    </div>
  );
}

/** Ready, or the first blocking reason, in RUN-003's own checked order. Absent when unresolved. */
interface AbilityChip {
  tone: "success" | "danger" | "warning" | "info";
  label: string;
}

function abilityChip(
  activityId: string,
  name: string,
  decl: AbilityDecl,
  activator: StateEntity | null,
  now: number,
): AbilityChip | null {
  if (activator === null) return null;
  const verdict = activationVerdict(decl, activityId, name, activator);
  if (verdict.allowed) return { tone: "success", label: "Ready" };
  switch (verdict.reason) {
    case "blocked_by_tag":
      return { tone: "danger", label: "Blocked" };
    case "missing_tag":
      return { tone: "warning", label: "Missing tag" };
    case "on_cooldown": {
      const active = cooldownEffect(activator, name);
      const remaining =
        active?.expires_at == null ? null : Math.max(0, Math.ceil(active.expires_at - now));
      return { tone: "info", label: remaining === null ? "Cooling" : `Cooling ${String(remaining)}s` };
    }
    case "cannot_afford":
      return { tone: "danger", label: "Cannot afford" };
    default:
      return null;
  }
}

function Ability({
  name,
  decl,
  chip,
}: {
  readonly name: string;
  readonly decl: AbilityDecl;
  readonly chip: AbilityChip | null;
}) {
  return (
    <div className="cx-row">
      <div className="cx-row-id">
        <div>
          <div className="cx-node-name">
            <Mono>{name}</Mono>
          </div>
          <div>
            <Muted small>
              {`${
                decl.cost == null
                  ? "free"
                  : `costs ${String(decl.cost.amount)} ${decl.cost.attribute}`
              }${decl.cooldown == null ? "" : `, ${String(decl.cooldown)}s cooldown`}`}
            </Muted>
          </div>
          {(decl.effects ?? []).length === 0 ? null : (
            <div>
              <Muted small>{`grants ${(decl.effects ?? []).join(", ")}`}</Muted>
            </div>
          )}
          {(decl.has ?? []).length === 0 ? null : (
            <div>
              <Muted small>{`needs ${(decl.has ?? []).join(", ")}`}</Muted>
            </div>
          )}
          {(decl.has_not ?? []).length === 0 ? null : (
            <div>
              <Muted small>{`blocked by ${(decl.has_not ?? []).join(", ")}`}</Muted>
            </div>
          )}
        </div>
      </div>
      {chip === null ? null : <StatusBadge tone={chip.tone}>{chip.label}</StatusBadge>}
    </div>
  );
}

export interface AbilityPanelProps {
  readonly activities: SpaceActivities;
  /** Narrowed to one added activity when one is selected, and the whole space otherwise. */
  readonly selectedId: string | null;
  /** Read the live activator through this canvas, the same one the stage is drawing. Granted
   * only to the space's owner; a moderator's read degrades to whatever their own role would see. */
  readonly canvasKind: string;
}

/** The one live entity holding this added activity's abilities, or null when that is ambiguous. */
function activatorFor(row: AddedActivityRow, readings: readonly MountReading[]): StateEntity | null {
  const reading = readings.find((one) => one.mountId === row.mount?.mount_id);
  if (reading === undefined || reading.entities.length !== 1) return null;
  return reading.entities[0] ?? null;
}

export function AbilityPanel({ activities, selectedId, canvasKind }: AbilityPanelProps) {
  const navigate = useNavigate();
  const createDraft = useCreateDraft();
  const [needle, setNeedle] = useState("");
  const [kind, setKind] = useState<"all" | "entities" | "effects" | "abilities">("all");
  const now = useNow();

  const declared = declaredIn(activities, selectedId).filter((one) =>
    holdsAnything(one.manifest),
  );

  if (activities.rows.length === 0) {
    return (
      <EmptyState
        bare
        headline="Nothing added"
        body="Add an activity and what it declares shows up here."
      />
    );
  }

  /** Editing what an activity declares is publishing its next version, which is the Studio. */
  const edit = (manifest: ActivityManifest): void => {
    createDraft.mutate(
      { ...manifest, activity: { ...manifest.activity, version: nextPatch(manifest.activity.version) } },
      {
        onSuccess: (draft) => {
          void navigate({ to: "/console/studio/$draft", params: { draft: draft.slug } });
        },
      },
    );
  };

  if (declared.length === 0) {
    return (
      <EmptyState
        bare
        headline="Nothing declares an entity, an effect or an ability"
        body="Plenty of activities declare none. The Studio is where a new one declares its own."
      />
    );
  }

  const wanted = (name: string): boolean =>
    needle === "" || name.toLowerCase().includes(needle.toLowerCase());

  return (
    <Stack gap={4}>
      <Stack gap={2}>
        <Input
          value={needle}
          placeholder="Find an entity, effect or ability"
          onChange={(event) => {
            setNeedle(event.target.value);
          }}
        />
        <Select
          aria-label="What to show"
          value={kind}
          options={[
            { value: "all", label: "everything" },
            { value: "entities", label: "entities only" },
            { value: "effects", label: "effects only" },
            { value: "abilities", label: "abilities only" },
          ]}
          onChange={(event) => {
            setKind(event.target.value as typeof kind);
          }}
        />
      </Stack>

      <LiveMounts rows={activities.rows} asCanvas={canvasKind}>
        {(readings) =>
          declared.map(({ row, manifest }) => {
            const entities = Object.entries(manifest.entities ?? {}).filter(([one]) => wanted(one));
            const effects = Object.entries(manifest.effects ?? {}).filter(([one]) => wanted(one));
            const abilities = Object.entries(manifest.abilities ?? {}).filter(([one]) => wanted(one));
            const activator = activatorFor(row, readings);
            const draftLabel = `${nextPatch(manifest.activity.version)} draft`;

            return (
              <Stack key={row.added_activity_id} gap={3}>
                <div className="cx-node-name">{row.name}</div>
                {kind !== "abilities" && kind !== "effects" && entities.length > 0 ? (
                  <Section title="Entities">
                    <Stack gap={2}>
                      {entities.map(([name, decl]) => (
                        <Entity key={name} name={name} decl={decl} />
                      ))}
                    </Stack>
                  </Section>
                ) : null}
                {kind !== "abilities" && kind !== "entities" && effects.length > 0 ? (
                  <Section title="Effects">
                    <Stack gap={2}>
                      {effects.map(([name, decl]) => (
                        <Effect key={name} name={name} decl={decl} />
                      ))}
                    </Stack>
                  </Section>
                ) : null}
                {kind !== "entities" && kind !== "effects" && abilities.length > 0 ? (
                  <Section title="Abilities">
                    <Stack gap={2}>
                      {abilities.map(([name, decl]) => (
                        <Ability
                          key={name}
                          name={name}
                          decl={decl}
                          chip={abilityChip(row.activity_id, name, decl, activator, now)}
                        />
                      ))}
                    </Stack>
                  </Section>
                ) : null}
                <Section title="Publisher">
                  <div className="cx-row">
                    <div className="cx-row-id">
                      <Muted small>{draftLabel}</Muted>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        edit(manifest);
                      }}
                    >
                      Edit as a new version
                    </Button>
                  </div>
                </Section>
              </Stack>
            );
          })
        }
      </LiveMounts>
    </Stack>
  );
}
